import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import { calculateRawAudioFrameBufferSize, calculateSampleCountForFrameNumber, calculateSampleOffsetForFrameNumber, RawAudioFormat } from '@mimir/common/transcoding/rawFrame';
import type { Fraction } from '@mimir/common/util/fractionType';
import type { AudioFrameV2, FrameInfoV2, FrameProviderV2, FrameV2, Listener, VideoFrameV2, VideoInfoV2, OpaqueRefCountedBuffer, PreloadInfo } from '@mimir/cutter/frameProvider/frameProvider';
import { addFractions, compareFractions, divideFractions, formatFractionAsExpression, frac, invertFraction, maxFractions, minFractions, multiplyFractionBy, subtractFractions, validateFraction } from "@mimir/common/util/integerMath";
import { approximateFractionValue, multiplyFractions } from '@mimir/common/util/integerMath';
import { createAudioCache, AudioCache } from './audioCache';
import { NO_LOGGER } from '@mimir/common/util/logging';
import { Logger } from '@mimir/common/types/logger';
import { invariant } from '@mimir/common/util/invariant';
import { LogEntry, PerformanceEntry, StructuredLogger } from '@mimir/primitive/structuredLogger';
import { exceptionToString } from '@mimir/common/util/exception';
import { isAbortError } from '@mimir/common/util/abortControl';
import { RefCountedBuffer, BufferCache } from '@mimir/cutter/frameProvider/refCountedBuffer';
import { abortify } from '@mimir/common/util/abortify';
import { analyzeReverseGopWarmUp, ReverseGopWarmUpInfo } from './reverseGopWarmUp';
import { closeFrame, infoFromFrame, makeBlackFrame, validateFrame } from '@mimir/cutter/frameProvider/frameUtilities';

export type VideoCodec = 'h264' | 'mpeg2' | 'test_video_codec';
export type AudioCodec = 'aac' | 'wav' | 'test_audio_codec';

export interface PacketProviderStreamInfoVideo {
  type: 'video',
  codec: VideoCodec,
  frameRate: Fraction,
  gopSize: number,
}

export interface PacketProviderStreamInfoAudio {
  type: 'audio',
  codec: AudioCodec,
  sampleRate: number,
  channelCount: number,
}

export type PacketProviderStreamInfo = PacketProviderStreamInfoVideo | PacketProviderStreamInfoAudio;

export interface PacketProviderInfo {
  duration: Fraction,
  streams: PacketProviderStreamInfo[],
}

export interface VideoPacket {
  type: 'video',
  streamIndex: number,
  codec: VideoCodec,
  keyFrame: boolean,
  pts: Fraction,
  dts: Fraction,
  duration: Fraction,
  payload: Uint8Array,

  // The optional binary data containing codec specific information
  description: Uint8Array | undefined;
}

export interface AudioPacket {
  type: 'audio',
  streamIndex: number,
  codec: AudioCodec,
  pts: Fraction,
  dts: Fraction,
  duration: Fraction,
  sampleRate: number,
  payload: Uint8Array,
}

export type Packet = VideoPacket | AudioPacket;

export interface PacketProvider {
  seek(logger: StructuredLogger, position: Fraction, abortSignal: AbortSignal): Promise<void>;
  poll(logger: StructuredLogger, abortSignal: AbortSignal): Promise<Packet | null>;
  preload(startPosition: Fraction, preloadInfo: PreloadInfo): Promise<void>;
  info(): PacketProviderInfo;
  bufferedRanges(): BufferRange[];
}

export interface DecodedVideoFrame {
  pts: Fraction,
  /**
   *  Set to true if this frame was decoded from a key frame
   */
  keyFrame: boolean,
  info: VideoInfoV2,
  buffer: OpaqueRefCountedBuffer,
}

export interface DecodedAudioFrame {
  audioFrame: AudioFrameV2,
}

export interface VideoDecoder {
  close(): void;
  push(packet: VideoPacket, abortSignal: AbortSignal): Promise<void>;
  poll(abortSignal: AbortSignal): Promise<DecodedVideoFrame | null>;
  flush(abortSignal: AbortSignal): Promise<void>;
  reset(): void;
}

export interface AudioDecoder {
  close(): void;
  push(packet: AudioPacket, abortSignal: AbortSignal): Promise<void>;
  poll(abortSignal: AbortSignal): Promise<DecodedAudioFrame | null>;
  flush(abortSignal: AbortSignal): Promise<void>;
  reset(): void;
}

export interface CreateVideoDecoderParameters {
  codec: VideoCodec,
}

export interface CreateAudioDecoderParameters {
  codec: AudioCodec,
  sampleRate: number,
  channelCount: number,
}

export interface DecoderFactory {
  createVideoDecoder(parameters: CreateVideoDecoderParameters): VideoDecoder;
  createAudioDecoder(parameters: CreateAudioDecoderParameters): AudioDecoder;
}

export interface DemuxAndDecodeFrameProviderStats {
  decodeRunCount: number,
}

export interface DemuxAndDecodeFrameProvider extends FrameProviderV2 {
  getStats(): DemuxAndDecodeFrameProviderStats;
}

export interface DemuxAndDecodeFrameProviderSettings {
  /**
   *  How many seconds before the current player position to keep
   *  decoded content for.
   */
  rewindCacheDuration: Fraction,

  /**
   *  How many seconds after the current player position to keep
   *  decoded content for.
   */
  forwardCacheDuration: Fraction,
}

export interface BufferRange {
  kind: 'demuxed' | 'decoded',
  start: Fraction,
  end: Fraction,
}

export interface FrameLogger extends StructuredLogger {
  debugReport(): string;
  performanceReport(): string;
}

export interface CreateDemuxAndDecodeFrameProviderParameters {
  logger: Logger,
  bufferCache: BufferCache,
  packetProvider: PacketProvider,
  decoderFactory: DecoderFactory,
  settings: DemuxAndDecodeFrameProviderSettings,
};

/**
 *  Creates a frame provider that coordinates a packetProvider and decoders.
 */
export function createDemuxAndDecodeFrameProvider(
  parameters: CreateDemuxAndDecodeFrameProviderParameters
) : DemuxAndDecodeFrameProvider {
  const { logger, packetProvider, decoderFactory, settings, bufferCache } = parameters;
  return new DemuxAndDecodeFrameProviderImpl(logger, packetProvider, decoderFactory, settings, bufferCache);
}

// ----

interface FractionRange {
  start: Fraction,
  end: Fraction,
}

class DemuxAndDecodeFrameProviderImpl implements DemuxAndDecodeFrameProvider {
  private frameRate : Fraction;
  private currentDecodeRun : DecodeRun | null = null;
  private streamStates : StreamState[] = [];
  private info : FrameInfoV2 | null = null;
  private closed : boolean = false;
  private busy : boolean = false;

  private highestFrameNumberWithContent: number = 0;
  private endEncounteredAtFrameNumber: number | null = null;

  private lastReportedBufferedRanges : BufferRange[] = [];

  private stats : DemuxAndDecodeFrameProviderStats = {
    decodeRunCount: 0,
  };

  private listener: Listener | null = null;

  // For frame rate / sample rate combinations where there are different number
  // of audio samples per frame we one silentBuffer for each sample count variant

  private activePreloading : Promise<void> | null = null;
  private pendingPreload : { position: Fraction, preloadInfo: PreloadInfo } | null = null;

  private log(logger: StructuredLogger, message: string, info: any = { }) {
    logger.log({ level: 'debug', message, prefix: ['DemuxAndDecodeFrameProvider'], ...info });
  }

  constructor(
    private logger: Logger,
    private packetProvider: PacketProvider,
    private decoderFactory: DecoderFactory,
    private settings: DemuxAndDecodeFrameProviderSettings,
    private bufferCache: BufferCache,
  ) {
    logger.log({ level: 'debug', message: 'PacketProviderInfo', packetProviderInfo: packetProvider.info() });

    this.frameRate = { numerator: 25, denominator: 1 };
    for (const stream of packetProvider.info().streams) {
      if (stream.type === 'video') {
        this.frameRate = stream.frameRate;
      }
    }
    let totalChannelCount = 0;
    let streamIndex = 0;
    for (const stream of packetProvider.info().streams) {
      if (stream.type === 'video') {
        this.streamStates.push(new VideoStreamState(`stream(v${streamIndex})`, stream.codec, this.frameRate, this.decoderFactory));
      }
      if (stream.type === 'audio') {
        this.streamStates.push(new AudioStreamState(`stream(a${streamIndex})`, logger, stream.codec, stream.sampleRate, stream.channelCount, this.frameRate, this.decoderFactory, this.bufferCache));
        totalChannelCount += stream.channelCount;
      }
      streamIndex += 1;
    }
  }

  public setListener(listener: Listener): void {
    this.listener = listener;
  }

  public async getInfo(abortSignal: AbortSignal): Promise<FrameInfoV2> {
    if (this.busy) {
      throw new Error('preload called while another operation was on-going');
    }

    try {
      this.busy = true;
      if (this.info === null) {
        const frame0 = await this.getFrameNoBusyCheck(0, null, abortSignal);
        this.info = infoFromFrame(frame0);
        closeFrame(frame0);
      }
      return this.info;
    } finally {
      this.busy = false;
    }
  }

  public close(): void {
    const frameLogger = new FrameLoggerImpl(this.logger.sillyModeEnabled() ? this.logger : null);
    this.closed = true;
    if (this.currentDecodeRun !== null) {
      this.currentDecodeRun.close(frameLogger);
      this.currentDecodeRun = null;
    }
    this.streamStates.splice(0).forEach((streamState) => streamState.close());
  }

  public getStats(): DemuxAndDecodeFrameProviderStats {
    return this.stats;
  }

  public async getFrame(frameNumber: number, preloadHint: PreloadInfo | null,  abortSignal: AbortSignal): Promise<FrameV2> {
    if (this.busy) {
      throw new Error('getFrame called while another operation was on-going');
    }
    try {
      this.busy = true;
      return await this.getFrameNoBusyCheck(frameNumber, preloadHint, abortSignal);
    } finally {
      this.busy = false;
    }
  }

  private async getFrameNoBusyCheck(frameNumber: number, preloadHint: PreloadInfo | null,  abortSignal: AbortSignal): Promise<FrameV2> {
    const frameLogger = new FrameLoggerImpl(this.logger.sillyModeEnabled() ? this.logger : null);
    try {
      if (this.closed) {
        throw new Error('getFrame() called after close()');
      }

      this.log(frameLogger, 'getFrame()', { frameNumber, preloadHint });

      if ((this.endEncounteredAtFrameNumber !== null) && (frameNumber >= this.endEncounteredAtFrameNumber)) {
        this.log(frameLogger, 'End already encountered. No need to check for a frame', { end: this.endEncounteredAtFrameNumber, frameNumber });
        return await this.createBlackFrame(frameLogger, frameNumber, abortSignal);
      }

      let exactPosition = this.positionFromFrameNumber(frameNumber);

      if (preloadHint) {
        this.startNonBlockingPreload(exactPosition, preloadHint);
        if (preloadHint.direction === 'backward') {
          await this.reverseLongGopWarmUp(frameLogger, frameNumber, abortSignal);
        }
      }


      let frame = await this.getFrameOrNull(frameLogger, frameNumber, abortSignal);

      if ((this.info === null) && (frame !== null)) {
        this.info = infoFromFrame(frame);
      }

      this.reportBufferedRangeChangeIfNeeded();

      if (frame === null) {
        this.log(frameLogger, 'Encountered end', { frameNumber });
        if (frameNumber <= this.highestFrameNumberWithContent) {
          throw new Error(`Encountered end at frame number ${frameNumber} even though this FrameProvider has been successfully able to provide frame ${this.highestFrameNumberWithContent} earlier`);
        }

        if (this.endEncounteredAtFrameNumber === null) {
          this.endEncounteredAtFrameNumber = frameNumber;
        } else {
          this.endEncounteredAtFrameNumber = Math.min(this.endEncounteredAtFrameNumber, frameNumber);
        }
        return await this.createBlackFrame(frameLogger, frameNumber, abortSignal);
      }
      this.highestFrameNumberWithContent = Math.max(this.highestFrameNumberWithContent, frame.frameNumber);

      validateFrame(frame);

      return frame;
    } catch (error) {
      if (isAbortError(error)) {
        if (this.currentDecodeRun !== null) {
          this.currentDecodeRun.close(frameLogger);
          this.currentDecodeRun = null;
        }
        throw error;
      }
      // TODO: Throw an error with the frame log so that the application layer can choose how to
      // present it.
      this.logger.error(`FrameLog:${frameLogger.debugReport()}`);
      throw new Error(`DemuxAndDecodeFrameProvider.getFrame failed: ${exceptionToString(error)}`);
    }
  }

  public async preload(
    frameStart: number,
    preloadInfo: PreloadInfo,
    signal: AbortSignal
  ): Promise<void> {
    if (this.busy) {
      throw new Error('preload called while another operation was on-going');
    }
    const frameLogger = new FrameLoggerImpl(this.logger.sillyModeEnabled() ? this.logger : null);
    try {
      this.busy = true;
      const exactPosition = this.positionFromFrameNumber(frameStart);
      await abortify(signal, () => this.packetProvider.preload(exactPosition, preloadInfo));

      if (preloadInfo.direction === 'forward') {
        this.evictTrailingNonKeyFramesFromCache(frameLogger);
      }
      closeFrame(await this.getFrameNoBusyCheck(frameStart, null, signal));
    } catch (error) {
      if (!isAbortError(error)) {
        this.logger.error(`preload failed. FrameLog:\n${frameLogger.debugReport()}`);
      }
      throw error;
    } finally {
      this.busy = false;
    }
  }

  private reportBufferedRangeChangeIfNeeded() {
    const bufferedRanges = this.getBufferedRanges();
    if (!isEqual(bufferedRanges, this.lastReportedBufferedRanges)) {
      this.listener?.onBufferedRangeChange(bufferedRanges);
      this.lastReportedBufferedRanges = bufferedRanges;
    }
  }

  private evictTrailingNonKeyFramesFromCache(frameLogger: FrameLogger): void {
    const cacheRange = this.getPartiallyCachedRange();
    if (cacheRange === null) {
      return;
    }
    for (const stream of this.streamStates) {
      let p = stream.getLastKeyFramePosition();

      if (p !== null) {
        stream.evictRangeFromCache(frameLogger, p, cacheRange.end);
      }
    }
  }

  private getDecodedRanges(): FractionRange[] {
    const decodedRangesForStreams : FractionRange[][] = [];
    for (const streamState of this.streamStates) {
      decodedRangesForStreams.push(streamState.getCachedRanges());
    }
    return intersectArrayOfArrayOfFractionRanges(decodedRangesForStreams);
  }

  private getBufferedRanges(): BufferRange[] {
    const bufferedRanges : BufferRange[] = [];

    bufferedRanges.push(...this.packetProvider.bufferedRanges());

    const decodedRanges = this.getDecodedRanges();
    bufferedRanges.push(...decodedRanges.map((decodedRange) => {
      const bufferRange : BufferRange = {
        kind: 'decoded',
        start: decodedRange.start,
        end: decodedRange.end,
      };
      return bufferRange;
    }));

    return bufferedRanges;
  }

  private async createBlackFrame(frameLogger: FrameLogger, frameNumber: number, abortSignal: AbortSignal): Promise<FrameV2> {
    // It's easier to create a black frame that matches with the frame provider
    // format by just getting hold of any valid frame and replace the buffers
    // with black and silence.
    if (this.info === null) {
      // If this frame provider has never provided a valid frame we fetch frame
      // 0 and use that.
      const frame0 = await this.getFrameOrNull(frameLogger, 0, abortSignal);
      if (frame0 === null) {
        // If we can't even get frame zero we are screwed.
        throw new Error('Unable to generate black frame. No frame returned for position 0');
      }
      this.info = infoFromFrame(frame0);
    }

    return makeBlackFrame(this.info, frameNumber, this.bufferCache);
  }

  private getGopSize(): number {
    let gopSize : number = 0;
    for (const stream of this.packetProvider.info().streams) {
      if (stream.type === 'video') {
        gopSize = Math.max(gopSize, stream.gopSize);
      }
    }
    return gopSize;
  }

  private getReverseLongGopWarmUpInfo(frameNumber: number): ReverseGopWarmUpInfo {
    const decodedRanges = this.getDecodedRanges()
      .map((decodedRange) => ({
        start: Math.ceil(approximateFractionValue(multiplyFractions(decodedRange.start, this.frameRate))),
        end: Math.floor(approximateFractionValue(multiplyFractions(decodedRange.end, this.frameRate))),
      }));

    let decodePosition : number | null = null;
    if (this.currentDecodeRun !== null) {
      const low = this.currentDecodeRun.decodePosition()?.low ?? null;
      if (low !== null) {
        decodePosition = Math.floor(approximateFractionValue(multiplyFractions(low, this.frameRate)));
      }
    }

    return { decodedRanges, frameNumber, decodePosition, gopSize: this.getGopSize() };
  }

  private async reverseLongGopWarmUp(
    frameLogger: FrameLogger,
    frameNumber: number,
    abortSignal: AbortSignal
  ): Promise<void> {
    this.log(frameLogger, 'Starting reverse long GOP warm up', { frameNumber });
    while (true) {
      const info = this.getReverseLongGopWarmUpInfo(frameNumber);
      const result = analyzeReverseGopWarmUp(info);
      this.log(frameLogger, 'Reverse long GOP iteration', { info, result });
      if (result === null) {
        break;
      }
      if (result.type == 'seek') {
        if (result.frameNumber < 0) {
          return;
        }
        if (this.currentDecodeRun !== null) {
          this.currentDecodeRun.close(frameLogger);
          this.currentDecodeRun = null;
        }
        const position = divideFractions(frac(result.frameNumber, 1), this.frameRate);
        this.currentDecodeRun = await DecodeRun.create(frameLogger, position, this.packetProvider, this.streamStates, abortSignal);
      }
      if (result.type === 'progress') {
        if (this.currentDecodeRun === null) {
          const position = divideFractions(frac(frameNumber, 1), this.frameRate);
          this.currentDecodeRun = await DecodeRun.create(frameLogger, position, this.packetProvider, this.streamStates, abortSignal);
        }
        const position = divideFractions(frac(frameNumber, 1), this.frameRate);
        const decodePosition = this.currentDecodeRun.decodePosition();
        if (decodePosition) {
          this.evictFromCache(frameLogger, position, decodePosition);
        }
        if (!await this.currentDecodeRun.processOnePacket(frameLogger, this.packetProvider, abortSignal)) {
          return;
        }

        abortSignal.throwIfAborted();
      }
    }
    this.log(frameLogger, 'Reverse long GOP warm up complete', { frameNumber });
  }

  private evictFromCache(frameLogger: FrameLogger, requestedPosition: Fraction, decodePosition: DecodePosition) {
    const gopSizeDuration = divideFractions(frac(this.getGopSize(), 1), this.frameRate);
    const gopProtection = subtractFractions(requestedPosition, multiplyFractionBy(gopSizeDuration, 2));

    const hotAreaLow = minFractions(minFractions(decodePosition.low, subtractFractions(requestedPosition, this.settings.rewindCacheDuration)), gopProtection);
    const hotAreaHigh = maxFractions(decodePosition.high, addFractions(requestedPosition, this.settings.forwardCacheDuration));

    const cacheRange = this.getPartiallyCachedRange();

    if (cacheRange === null) {
      // Nothing in cache
      return;
    }

    this.evictRangeFromCache(frameLogger, cacheRange.start, hotAreaLow);
    this.evictRangeFromCache(frameLogger, hotAreaHigh, cacheRange.end);
  }

  /**
   * Returns the union of all cache ranges for all streams. This means that the
   * returned range may not be cached for all stream but will at least be
   * cached for one stream
   */
  private getPartiallyCachedRange(): FractionRange | null {
    let start : Fraction = { numerator: 1, denominator: 0 };
    let end : Fraction = { numerator: -1, denominator: 0 };

    for (const streamState of this.streamStates) {
      const streamCachedRanges = streamState.getCachedRanges();
      if (streamCachedRanges.length > 0) {
        const firstRange = streamCachedRanges[0];
        const lastRange = streamCachedRanges[streamCachedRanges.length - 1];
        start = minFractions(start, firstRange.start);
        end = maxFractions(end, lastRange.end);
      }
    }

    if ((start.denominator === 0) || (end.denominator === 0)) {
      return null;
    }
    return { start, end };
  }

  private evictRangeFromCache(frameLogger: FrameLogger, start: Fraction, end: Fraction) {
    if (compareFractions(start, end) >= 0) {
      return;
    }

    this.log(frameLogger, 'evictRangeFromCache', { start, end });
    for (const streamState of this.streamStates) {
      streamState.evictRangeFromCache(frameLogger, start, end);
    }
  }

  private startPendingPreload() {
    if ((this.activePreloading !== null) || (this.pendingPreload === null)) {
      return;
    }

    const { position, preloadInfo } = this.pendingPreload;
    this.pendingPreload = null;

    this.activePreloading =
      this.packetProvider.preload(position, preloadInfo)
        .catch((error) => this.logger.error(`Error during preload: ${exceptionToString(error)}`))
        .finally(() => {
          this.activePreloading = null;
          this.startPendingPreload();
        });
  }

  private startNonBlockingPreload(position: Fraction, preloadInfo: PreloadInfo) {
    this.pendingPreload = { position, preloadInfo };
    this.startPendingPreload();
  }

  private async getFrameOrNull(
    frameLogger: FrameLogger,
    frameNumber: number,
    abortSignal: AbortSignal,
  ): Promise<FrameV2 | null> {
    let exactPosition = this.positionFromFrameNumber(frameNumber);

    let exactEndOfFrame = this.positionFromFrameNumber(frameNumber + 1);
    let decodeRunCreatedForThisFrame = false;
    this.log(frameLogger, 'getFrameOrNull()', { frameNumber, exactPosition });
    while (true) {
      const decodedContentForFrame = this.getDecodedContentForFrame(frameLogger, frameNumber);
      if (decodedContentForFrame !== null) {
        this.log(frameLogger, 'getFrameOrNull: Got decoded content');
        return flattenDecodedContent(frameNumber, this.frameRate, decodedContentForFrame, this.bufferCache);
      }
      if (this.currentDecodeRun) {
        let decodePosition = this.currentDecodeRun.decodePosition();

        const resetDecodeRunTolerance : Fraction = { numerator: 1, denominator: 2 };

        if (decodePosition !== null) {
          this.evictFromCache(frameLogger, exactPosition, decodePosition);
        }

        this.log(frameLogger, 'getFrameOrNull: has decode run', { exactPosition, exactEndOfFrame, decodePosition });

        if ((compareFractions(this.currentDecodeRun.startPosition(), exactPosition) !== 0) && (decodePosition !== null) && (compareFractions(addFractions(decodePosition.high, resetDecodeRunTolerance), exactEndOfFrame) <= 0)) {
          this.log(frameLogger, 'Seeking too far ahead to keep going with current decode run. Resetting.');
          this.currentDecodeRun.close(frameLogger);
          this.currentDecodeRun = null;
        }

        // If the current decode position have moved past the requested
        // position yet we don't have decoded content for the requested
        // position it means that we need to start another decode run.
        else if ((decodePosition !== null) && (compareFractions(exactEndOfFrame, decodePosition.low) <= 0)) {
          this.log(frameLogger, 'getFrameOrNull: Current decode run has gone past requested position. Clearing decode run.', { exactEndOfFrame, decodePositionLow: decodePosition.low });

          // We don't have decoded media for the requested position and the
          // current decode run has already passed this position. This means
          // we need to start a new decode run.
          this.currentDecodeRun.close(frameLogger);
          this.currentDecodeRun = null;
        }
      }

      if (this.currentDecodeRun === null) {
        this.log(frameLogger, 'getFrameOrNull: Creating new decode run');

        // If the current decode run was created in order to obtain the
        // current frame but the decode run was unable to do so there is not
        // point in trying over and over again to start a new decode run for
        // this frame. Instead we just fail. This means that the
        // PackerProvider is not providing the necessary packets to provide
        // decoded content for the requested frame number. I.e the
        // PacketProvider is incorrectly implemented.
        if (decodeRunCreatedForThisFrame) {
          throw new Error(`DecodeRunStarted at position ${approximateFractionValue(exactPosition)} missed the requested frame`);
        }

        decodeRunCreatedForThisFrame = true;
        this.currentDecodeRun = await DecodeRun.create(frameLogger, exactPosition, this.packetProvider, this.streamStates, abortSignal);
        this.stats.decodeRunCount += 1;
      }

      if (!await this.currentDecodeRun.processOnePacket(frameLogger, this.packetProvider, abortSignal)) {
        this.log(frameLogger, 'getFrameOrNull: Returning null: reached end of packetProvider');
        this.currentDecodeRun.close(frameLogger);
        this.currentDecodeRun = null;
        return null;
      }
    }
  }

  private positionFromFrameNumber(frameNumber: number): Fraction {
    return divideFractions({ numerator: frameNumber, denominator: 1 }, this.frameRate);
  }

  private getDecodedContentForFrame(frameLogger: FrameLogger, frameNumber: number): DecodedContentForFrame | null {
    this.log(frameLogger, 'getDecodedContentForFrame()', { frameNumber });
    let streamIndex = 0;
    for (const streamState of this.streamStates) {
      if (!streamState.hasDecodedContentForFrame(frameLogger, frameNumber)) {
        this.log(frameLogger, 'No decoded content for stream', { streamIndex });
        return null;
      }
      streamIndex += 1;
    }

    const decodedContentForFrame : DecodedContentForFrame = {
      video: [],
      audio: [],
    }
    for (const streamState of this.streamStates) {
      streamState.getDecodedContentForFrame(frameLogger, frameNumber, decodedContentForFrame);
    }

    return decodedContentForFrame;
  }
}

interface DecodedContentForFrame {
  video: VideoFrameV2[],
  audio: AudioFrameV2[],
}

interface AudioFrameInfo {
  sampleCount: number,
  format: RawAudioFormat,
  sampleRate: number,
  channelCount: number,
}

function getAudioInfoFromDecodedContentForFrame(decodedContent: DecodedContentForFrame): AudioFrameInfo | null {
  let info : AudioFrameInfo | null = null;
  for (const audio of decodedContent.audio) {
    if (info === null) {
      info = {
        sampleCount: audio.sampleCount,
        format: audio.format,
        sampleRate: audio.sampleRate,
        channelCount: audio.channelCount,
      };
    } else {
      if (info.sampleCount !== audio.sampleCount) {
        throw new Error(`Inconsistent sample count between audio streams: ${info.sampleRate} != ${audio.sampleCount}`);
      }
      if (info.format !== audio.format) {
        throw new Error(`Inconsistent sample count between audio streams: ${info.format} != ${audio.format}`);
      }
      if (info.sampleRate !== audio.sampleRate) {
        throw new Error(`Inconsistent sample rate between audio streams: ${info.sampleRate} != ${audio.sampleRate}`);
      }
      info.channelCount += audio.channelCount;
    }
  }
  return info;
}

function mergeAudioBuffers(
  audioInfo: AudioFrameInfo,
  rawAudioFrames: AudioFrameV2[],
  bufferCache: BufferCache,
): RefCountedBuffer {
  const audioBufferSize = calculateRawAudioFrameBufferSize(audioInfo, audioInfo.sampleCount);
  switch (audioInfo.format) {
    case 'pcm16': {
      return bufferCache.allocateBuffer(
        audioBufferSize,
        (buffer: Uint8Array) => {
          const target = new Int16Array(buffer.buffer);
          for (const audio of rawAudioFrames) {
            const source = audio.buffer.asReadonlyInt16Array();
            let srcOffset = 0;
            let dstOffset = 0;
            for (let j = 0; j < audio.sampleCount; j++) {
              dstOffset = (j * audioInfo.channelCount);
              for (let i = 0; i < audio.channelCount; i++) {
                target[dstOffset] = source[srcOffset];
                srcOffset += 1;
                dstOffset += 1;
              }
            }
            audio.buffer.close();
          }
        }
      );
    }
    case 'float32': {
      return bufferCache.allocateBuffer(
        audioBufferSize,
        (buffer: Uint8Array) => {
          const target = new Float32Array(buffer.buffer);
          let channelOffset = 0;
          for (const audio of rawAudioFrames) {
            const source = audio.buffer.asReadonlyFloat32Array();
            let srcOffset = 0;
            let dstOffset = 0;
            for (let j = 0; j < audio.sampleCount; j++) {
              dstOffset = (j * audioInfo.channelCount) + channelOffset;
              for (let i = 0; i < audio.channelCount; i++) {
                target[dstOffset] = source[srcOffset];
                srcOffset += 1;
                dstOffset += 1;
              }
            }
            channelOffset += audio.channelCount;
            audio.buffer.close();
          }
          return new Uint8Array(target.buffer);
        }
      );
    }
  }
}

function flattenDecodedContent(
  frameNumber: number,
  frameRate: Fraction,
  decodedContent: DecodedContentForFrame,
  bufferCache: BufferCache,
): FrameV2 {
  const rawFrame : FrameV2 = {
    frameRate,
    frameNumber,
    video: null,
    audio: null,
  };

  if (decodedContent.video.length > 1) {
    throw new Error('Multiple video streams not supported');
  }

  if (decodedContent.video.length === 1) {
    rawFrame.video = decodedContent.video[0];
  }

  const audioInfo = getAudioInfoFromDecodedContentForFrame(decodedContent);

  if (audioInfo !== null) {
    const buffer = mergeAudioBuffers(audioInfo, decodedContent.audio, bufferCache);
    const sampleOffset = calculateSampleOffsetForFrameNumber(audioInfo.sampleRate, frameRate, frameNumber);

    rawFrame.audio = {
      sampleRate: audioInfo.sampleRate,
      sampleCount: audioInfo.sampleCount,
      sampleOffset,
      channelCount: audioInfo.channelCount,
      format: audioInfo.format,
      buffer,
    };
  }
  return rawFrame;
}

interface StreamState {
  createDecodeRunStreamState(): DecodeRunStreamState;

  // Returns true if the required decoded content is available for the given frame.
  hasDecodedContentForFrame(frameLogger: FrameLogger, frameNumber: number): boolean;

  // Pushed the decoded content of this stream onto the DecodedContentForFrame.
  // Will throw an exception if there is no decoded content available for this
  // frame.
  getDecodedContentForFrame(frameLogger: FrameLogger, frameNumber: number, target: DecodedContentForFrame): void;

  /**
   *  Returns the current range in the decoded content cache for this stream
  */
  getCachedRanges(): FractionRange[];

  evictRangeFromCache(frameLogger: FrameLogger, start: Fraction, end: Fraction): void;

  /**
   *  Returns the position of the last cache entry which was a key frame. If
   *  the underlying Codec is not long gop it should return null.
   */
  getLastKeyFramePosition(): Fraction | null;

  close(): void;
}

interface VideoStreamCacheEntry {
  frameNumber: number,
  info: VideoInfoV2,
  buffer: OpaqueRefCountedBuffer,
  keyFrame: boolean,
}

class VideoStreamState implements StreamState {
  private decodeCache : VideoStreamCacheEntry[] = [];
  private decoder : VideoDecoder;

  constructor(
    private logPrefix: string,
    codec: VideoCodec,
    private frameRate: Fraction,
    decoderFactory: DecoderFactory,
  ) {
    this.decoder = decoderFactory.createVideoDecoder({ codec });
  }

  private log(frameLogger: StructuredLogger, message: string, info: any = { }) {
    frameLogger.log({ level: 'debug', prefix: [this.logPrefix], message, ...info });
  }

  createDecodeRunStreamState(): DecodeRunStreamState {
    return new DecodeRunVideoStreamState(
      `${this.logPrefix}/decode`,
      this.decoder,
      this.frameRate,
      (decodedVideoFrame: DecodedVideoFrame, frameNumber: number) => {

        // Remove duplicate entry if frame is already in cache
        this.decodeCache = this.decodeCache
          .filter((f) => {
            if (f.frameNumber === frameNumber) {
              f.buffer.close();
              return false;
            } else {
              return true;
            }
          });

        this.decodeCache.push({
          frameNumber,
          info: decodedVideoFrame.info,
          buffer: decodedVideoFrame.buffer,
          keyFrame: decodedVideoFrame.keyFrame,
        });
        this.decodeCache.sort((a, b) => a.frameNumber - b.frameNumber);
      }
    );
  }

  hasDecodedContentForFrame(frameLogger: FrameLogger, frameNumber: number): boolean {
    this.log(frameLogger, 'hasDecodedContentForFrame()', { frameNumber, frames: this.decodeCache.map(f => f.frameNumber) });
    const entry = this.decodeCache.find((f) => f.frameNumber === frameNumber);
    return !!entry;
  }

  getDecodedContentForFrame(
    frameLogger: FrameLogger,
    frameNumber: number,
    target: DecodedContentForFrame,
  ) {
    this.log(frameLogger, 'getDecodedContentForFrame()', { frameNumber, frames: this.decodeCache.map(f => f.frameNumber) });
    const entry = this.decodeCache.find((f) => f.frameNumber === frameNumber);
    if (!entry) {
      throw new Error(`No decoded content for frame: ${frameNumber}`);
    }
    target.video.push({
      ...entry.info,
      buffer: entry.buffer.addReference(),
    });
  }

  getCachedRanges(): FractionRange[] {
    const outputRanges : { start: number, end: number }[] = [];
    let currentRange : { start: number, end: number } | null = null;
    for (const entry of this.decodeCache) {
      if (currentRange !== null) {
        if (currentRange.end != entry.frameNumber) {
          outputRanges.push(currentRange);
          currentRange = null;
        } else {
          currentRange.end = entry.frameNumber + 1;
          continue;
        }
      }
      currentRange = { start: entry.frameNumber, end: entry.frameNumber + 1 };
    }

    if (currentRange !== null) {
      outputRanges.push(currentRange);
      currentRange = null;
    }

    return outputRanges.map((frameNumberRange) => {
      const startAsFraction = {
        numerator: frameNumberRange.start,
        denominator: 1,
      };
      const endAsFraction = {
        numerator: frameNumberRange.end,
        denominator: 1,
      };
      return {
        start: divideFractions(startAsFraction, this.frameRate),
        end: divideFractions(endAsFraction, this.frameRate),
      };
    });
  }

  evictRangeFromCache(frameLogger: FrameLogger, start: Fraction, end: Fraction): void {
    const frameStart = Math.ceil(approximateFractionValue(multiplyFractions(start, this.frameRate)));
    const frameEnd = Math.floor(approximateFractionValue(multiplyFractions(end, this.frameRate)));
    this.decodeCache = this.decodeCache
      .filter((entry) => {
        if ((entry.frameNumber < frameStart) || (entry.frameNumber > frameEnd)) {
          return true;
        } else {
          this.log(frameLogger, 'Frame from cache', { frameNumber: entry.frameNumber });
          entry.buffer.close();
          return false;
        }
      });
  }

  close() {
    for (const entry of this.decodeCache.splice(0)) {
      entry.buffer.close();
    }
    this.decoder.close();
  }

  getLastKeyFramePosition(): Fraction | null {
    let i = this.decodeCache.length;
    while (i > 0) {
      i--;
      if (this.decodeCache[i].keyFrame) {
        return divideFractions(frac(this.decodeCache[i].frameNumber, 1), this.frameRate);
      }
    }
    return null;
  }
}

class AudioStreamState implements StreamState {
  private decodedCache : AudioCache;
  private decoder : AudioDecoder;

  constructor(
    private logPrefix: string,
    private logger: Logger,
    codec: AudioCodec,
    private sampleRate: number,
    channelCount: number,
    private frameRate: Fraction,
    decoderFactory: DecoderFactory,
    bufferCache: BufferCache,
  ) {
    this.decodedCache = createAudioCache(NO_LOGGER, bufferCache);
    this.decoder = decoderFactory.createAudioDecoder({ codec, sampleRate, channelCount });
  }

  private log(frameLogger: StructuredLogger, message: string, info: any = { }) {
    frameLogger.log({ level: 'debug', prefix: [this.logPrefix], message, ...info });
  }

  createDecodeRunStreamState(): DecodeRunAudioStreamState {
    return new DecodeRunAudioStreamState(
      `${this.logPrefix}/decode`,
      this.decoder,
      (frameLogger: FrameLogger, decodedAudioFrame: DecodedAudioFrame) => {
        this.decodedCache.push(decodedAudioFrame.audioFrame);
        const { sampleOffset, sampleCount } = decodedAudioFrame.audioFrame;
        this.log(frameLogger, 'pushed decoded samples to cache', { sampleOffset, sampleCount, ranges: this.decodedCache.ranges() });
      }
    );
  }

  hasDecodedContentForFrame(frameLogger: FrameLogger, frameNumber: number): boolean {
    const sampleRate = this.decodedCache.sampleRate();
    if (sampleRate === null) {
      return false;
    }

    const sampleOffset = calculateSampleOffsetForFrameNumber(sampleRate, this.frameRate, frameNumber);
    const sampleCount = calculateSampleCountForFrameNumber(sampleRate, this.frameRate, frameNumber);

    this.log(frameLogger, 'hasDecodedContentForFrame()', { frameNumber, sampleOffset, sampleCount, ranges: this.decodedCache.ranges() });

    return this.decodedCache.has(sampleOffset, sampleCount);
  }

  getDecodedContentForFrame(
    frameLogger: FrameLogger,
    frameNumber: number,
    target: DecodedContentForFrame
  ): void {
    const sampleRate = this.decodedCache.sampleRate();
    if (sampleRate === null) {
      throw new Error(`Decoded content not available from frame ${frameNumber}.`);
    }

    const sampleOffset = calculateSampleOffsetForFrameNumber(sampleRate, this.frameRate, frameNumber);
    const sampleCount = calculateSampleCountForFrameNumber(sampleRate, this.frameRate, frameNumber);

    this.log(frameLogger, 'getDecodedContentForFrame()', { frameNumber, sampleOffset, sampleCount, ranges: this.decodedCache.ranges() });

    const audioFrame = this.decodedCache.get(sampleOffset, sampleCount);
    if (audioFrame === null) {
      throw new Error(`Decoded content not available from frame ${frameNumber}.`);
    }
    target.audio.push(audioFrame);
  }

  getCachedRanges(): FractionRange[] {
    return this.decodedCache.ranges()
      .map((sampleRange) => {
        return {
          start: { numerator: sampleRange.start, denominator: this.sampleRate },
          end: { numerator: sampleRange.end, denominator: this.sampleRate },
        };
      });
  }

  evictRangeFromCache(frameLogger: FrameLogger, start: Fraction, end: Fraction): void {
    const sampleRateAsFraction : Fraction = { numerator: this.sampleRate, denominator: 1 };
    const sampleStart = Math.ceil(approximateFractionValue(multiplyFractions(start, sampleRateAsFraction)));
    const sampleEnd = Math.floor(approximateFractionValue(multiplyFractions(end, sampleRateAsFraction)));
    this.log(frameLogger, 'evicting sample range', { start, end });
    this.decodedCache.remove(sampleStart, sampleEnd);
  }

  close() {
    this.logger.info('Closing audio cache');
    this.decodedCache.close();
    this.decoder.close();
  }

  getLastKeyFramePosition(): Fraction | null {
    return null;
  }
}

interface DecodeRunStreamState {
  /**
   *  Return true unless null is passed and nothing is returned from the decoder
   */
  processPacket(frameLogger: FrameLogger, packet: Packet | null, abortSignal: AbortSignal): Promise<boolean>;

  /**
   *  Returns the expected position of the next decoded content to be put into
   *  the cache. If this decode run stream state has not decoded any content
   *  yet we don't know the decode position and return null
   */
  decodePosition(): Fraction | null;

  close(frameLogger: FrameLogger): void;
}

class DecodeRunVideoStreamState implements DecodeRunStreamState {
  private position : Fraction | null = null;
  private decoderFlushed : boolean = false;
  private lastKeyFramePosition : Fraction | null = null;
  private gopSize : Fraction = { numerator: 0, denominator: 1 };
  private frameDuration : Fraction;

  constructor(
    private logPrefix: string,
    private decoder: VideoDecoder,
    private frameRate: Fraction,
    private onDecodedVideoFrame: (decodedVideoFrame: DecodedVideoFrame, frameNumber: number) => void,
  ) {
    this.frameDuration = invertFraction(frameRate);
  }

  private log(frameLogger: StructuredLogger, message: string, info: any = { }) {
    frameLogger.log({ level: 'debug', prefix: [this.logPrefix], message, ...info });
  }

  async processPacket(
    frameLogger: FrameLogger,
    packet: Packet | null,
    abortSignal: AbortSignal,
  ): Promise<boolean > {
    if (packet !== null) {
      if (this.decoderFlushed) {
        throw new Error('Packet passed into flushed DecodeRunVideoStreamState');
      }

      if (packet.type !== 'video') {
        throw new Error(`${packet.type} packet passed to video stream state`);
      }

      if (this.lastKeyFramePosition !== null) {
        let deltaFromLastKeyFrame = subtractFractions(packet.dts, this.lastKeyFramePosition);
        this.gopSize = maxFractions(this.gopSize, deltaFromLastKeyFrame);
      }

      if (packet.keyFrame) {
        this.lastKeyFramePosition = packet.dts;
      }

      this.log(frameLogger, 'processPacket(): passing to decoder');
      await this.decoder.push(packet, abortSignal);
    } else if (!this.decoderFlushed) {
      this.log(frameLogger, 'processPacket(): flushing decoder');
      await this.decoder.flush(abortSignal);
      this.decoderFlushed = true;
    }
    this.log(frameLogger, 'processPacket(): polling decoder');
    let gotFrameCount = 0;
    while (true) {
      const frame = await this.decoder.poll(abortSignal);
      if (frame === null) {
        break;
      }
      gotFrameCount ++;
      const frameNumber =
        Math.round(approximateFractionValue(multiplyFractions(frame.pts, this.frameRate)))

      this.position = multiplyFractionBy(this.frameDuration, frameNumber + 1);
      this.log(frameLogger, 'processPacket(): got decoded video frame', { pts: frame.pts, newDecodePosition: { ...this.position } });
      this.onDecodedVideoFrame(frame, frameNumber);
    }

    return (packet !== null) || (gotFrameCount > 0);
  }

  decodePosition(): Fraction | null {
    return this.position;
  }

  close(frameLogger: FrameLogger): void {
    this.log(frameLogger, 'Resetting video decoder');
    this.decoder.reset();
  }
}

class DecodeRunAudioStreamState implements DecodeRunStreamState {
  private position : Fraction | null = null;
  private decoderFlushed : boolean = false;

  constructor(
    private logPrefix: string,
    private decoder: AudioDecoder,
    private onDecodedAudioFrame: (frameLogger: FrameLogger, decodedAudioFrame: DecodedAudioFrame) => void,
  ) { }

  private log(frameLogger: StructuredLogger, message: string, info: any = { }) {
    frameLogger.log({ level: 'debug', prefix: [this.logPrefix], message, ...info });
  }

  async processPacket(
    frameLogger: FrameLogger,
    packet: Packet| null,
    abortSignal: AbortSignal
  ): Promise<boolean> {
    this.log(frameLogger, 'processPacket()');
    if (packet !== null) {
      if (this.decoderFlushed) {
        throw new Error('Packet passed into flushed DecodeRunAudioStreamState');
      }
      if (packet.type !== 'audio') {
        throw new Error(`${packet.type} packet passed to audio stream state`);
      }
      this.log(frameLogger, 'processPacket: sending packet to decoder');
      await this.decoder.push(packet, abortSignal);
    } else if (!this.decoderFlushed) {
      this.log(frameLogger, 'processPacket: flushing decoder');
      await this.decoder.flush(abortSignal);
      this.decoderFlushed = true;
    }

    this.log(frameLogger, 'processPacket: polling decoder');
    let gotFrameCount = 0;
    while (true) {
      const frame = await this.decoder.poll(abortSignal);
      if (frame === null) {
        break;
      }

      gotFrameCount ++;

      this.position = {
        numerator: frame.audioFrame.sampleOffset + frame.audioFrame.sampleCount,
        denominator: frame.audioFrame.sampleRate
      };

      this.log(frameLogger, 'processPacket: got decoded audio frame', { sampleOffset: frame.audioFrame.sampleOffset, sampleCount: frame.audioFrame.sampleCount, newDecodePosition: { ...this.position } });

      this.onDecodedAudioFrame(frameLogger, frame);
    }

    return (packet !== null) || (gotFrameCount > 0);
  }

  decodePosition(): Fraction | null {
    return this.position;
  }

  close(frameLogger: FrameLogger): void {
    this.log(frameLogger, 'Resetting audio decoder');
    this.decoder.reset();
  }
}

/**
 *  Decode position is the current next point in time in which you expect
 *  decoded content from the decoder during a decode run. When decoding
 *  multiple streams there is not a single decode position, but rather one
 *  for each stream. What we are normally interested in is the highest and
 *  lowest decode position between the streams.
 */
interface DecodePosition {
  low: Fraction,
  high: Fraction,
}

class DecodeRun {
  private startedAtPosition : Fraction;
  private constructor(
    position: Fraction,
    private decodeRunStreamStates: DecodeRunStreamState[],
  ) {
    this.startedAtPosition = position;
  }

  public static async create(
    frameLogger: FrameLogger,
    position: Fraction,
    packetProvider: PacketProvider,
    streamStates: StreamState[],
    abortSignal: AbortSignal,
  ): Promise<DecodeRun> {
    await packetProvider.seek(frameLogger, position, abortSignal);

    const decodeRunStreamStates : DecodeRunStreamState[] = [];
    for (const streamState of streamStates) {
      decodeRunStreamStates.push(streamState.createDecodeRunStreamState());
    }

    return new DecodeRun(position, decodeRunStreamStates);
  }

  private log(frameLogger: StructuredLogger, message: string, info: any = { }) {
    frameLogger.log({ level: 'debug', prefix: ['DecodeRun'], message, ...info });
  }

  public startPosition(): Fraction {
    return this.startedAtPosition;
  }

  public async processOnePacket(
    frameLogger: FrameLogger,
    packetProvider: PacketProvider,
    abortSignal: AbortSignal,
  ): Promise<boolean> {
    this.log(frameLogger, 'processOnePacket()');
    const packet = await packetProvider.poll(frameLogger, abortSignal);
    if (packet === null) {
      this.log(frameLogger, 'processOnePacket: flushing decode stream states');
      for (const decodeStreamState of this.decodeRunStreamStates) {
        if (await decodeStreamState.processPacket(frameLogger, null, abortSignal)) {
          this.log(frameLogger, 'processOnePacket: processed packet during flush');
          return true;
        }
      }
      this.log(frameLogger, 'processOnePacket: no more packet to process');
      return false;
    }
    this.log(frameLogger, 'processOnePacket: Got packet from packet provider', { packet: { ...packet, payload: undefined, description: undefined } });

    if ((packet.streamIndex < 0) || (packet.streamIndex >= this.decodeRunStreamStates.length)) {
      throw new Error(`Got packet for unexpected stream index: ${packet.streamIndex}`);
    }
    this.log(frameLogger, 'processOnePacket: passing to decode run stream state');
    return await this.decodeRunStreamStates[packet.streamIndex].processPacket(frameLogger, packet, abortSignal);
  }

  /**
   * Returns the current decode position for this decode run. See comment on
   * DecodePosition for more information. If either of the streams have not
   * decoded anything the decode position remains undefined and this function
   * returns null.
   */
  public decodePosition(): DecodePosition | null {
    let position : DecodePosition | null = null;
    for (const streamState of this.decodeRunStreamStates) {
      const decodePositionForStream = streamState.decodePosition();
      if (decodePositionForStream === null) {
        return null;
      } else if (position === null) {
        position = {
          low: { ...decodePositionForStream },
          high: { ...decodePositionForStream },
        };
      } else {
        position = {
          low: minFractions(position.low, decodePositionForStream),
          high: maxFractions(position.high, decodePositionForStream),
        };
      }
    }
    return position;
  }

  public close(frameLogger: FrameLogger) {
    this.log(frameLogger, 'Closing DecodeRun..');
    for (const streamState of this.decodeRunStreamStates) {
      streamState.close(frameLogger);
    }
    this.log(frameLogger, 'Decode run closed');
  }
}

function intersectArrayOfArrayOfFractionRanges(arrayOfRanges: FractionRange[][]): FractionRange[] {
  const outputRanges : FractionRange[] = [];
  let deltaArray : { value: Fraction, overlapDelta: number }[] = [];
  for (const ranges of arrayOfRanges) {
    for (const range of ranges) {
      deltaArray.push({ value: range.start, overlapDelta: 1 });
      deltaArray.push({ value: range.end, overlapDelta: -1 });
    }
  }
  deltaArray.sort((a, b) => compareFractions(a.value, b.value));

  let currentOverlapCount = 0;
  let requiredOverlapCount = arrayOfRanges.length;
  let rangeStart : Fraction | null = null;
  for (const deltaEntry of deltaArray) {
    currentOverlapCount += deltaEntry.overlapDelta;

    // The only way we can have more overlap than the number of input ranges is that there is
    // overlap within the ranges themselves
    invariant(currentOverlapCount <= requiredOverlapCount, 'Input arrays have overlapping entries');
    invariant(currentOverlapCount >= 0, 'Sanity check failed. Logic error in range intersect calculations.');

    if (rangeStart === null) {

      if (currentOverlapCount == requiredOverlapCount) {
        rangeStart = deltaEntry.value;
      }
    } else {
      if (currentOverlapCount < requiredOverlapCount) {
        outputRanges.push({ start: rangeStart, end: deltaEntry.value });
        rangeStart = null;
      }
    }
  }

  return outputRanges;
}

class FrameLoggerImpl implements FrameLogger {
  frameStart = Date.now();
  entries: LogEntry[] = [];

  constructor(private forwardTo: Logger | null) { }

  log(entry: LogEntry): void {
    this.entries.push(entry);
    if (this.forwardTo && (entry.level !== 'debug' || (this.forwardTo.debugModeEnabled() || this.forwardTo.sillyModeEnabled()))) {
      switch (entry.level) {
        case 'debug': {
          if (this.forwardTo.debugModeEnabled() || this.forwardTo.sillyModeEnabled()) {
            this.forwardTo.debug(FrameLoggerImpl.formatLogEntry(entry));
          }
          break;
        }
        case 'info':
          this.forwardTo.info(FrameLoggerImpl.formatLogEntry(entry));
          break;
        case 'warn':
          this.forwardTo.warn(FrameLoggerImpl.formatLogEntry(entry));
          break;
        case 'error':
        case 'alert':
          this.forwardTo.error(FrameLoggerImpl.formatLogEntry(entry));
          break;
      }
    }
  }

  debugReport(): string {
    const lines : string[] = [];
    for (const entry of this.entries) {
      if (entry.type === 'performance') {
        continue;
      }
      lines.push(FrameLoggerImpl.formatLogEntry(entry));
    }
    return lines.join('\n');
  }

  performanceReport(): string {
    const lines : string[] = [];
    for (const entry of this.entries) {
      const line = FrameLoggerImpl.performanceLogEntry(entry);
      if (line !== null) {
        lines.push(line);
      }
    }
    return lines.join('\n');
  }

  async close(): Promise<void> {
    if (this.forwardTo) {
      await this.forwardTo.flush();
    }
  }

  static formatLogEntry(entry: LogEntry): string {
    const { message, prefix } = entry;

    const extraInfo : LogEntry = { ...entry };
    delete extraInfo['message'];
    delete extraInfo['prefix'];
    delete extraInfo['level'];

    let msg = '';
    if (prefix) msg += `[${prefix.join('/')}] `;
    if (message) msg += message;
    if (Object.keys(extraInfo).length > 0) {
      msg += ` ${FrameLoggerImpl.formatValue(extraInfo)}`;
    }
    return msg;
  }

  static performanceLogEntry(entry: LogEntry): string | null {
    if (!entry.performance) {
      return null;
    }

    const performance : PerformanceEntry = entry.performance as any;

    return `${performance.what}: ${performance.endTimeMs - performance.startTimeMs} ms`;
  }

  static formatValue(u: unknown): string {
    if (typeof u === 'string') { return u };
    if (typeof u === 'number') { return u.toString(); }
    if (typeof u === 'boolean') { return u.toString(); }
    if (typeof u === 'object' && !!u) {
      if (isArray(u)) {
        return `[${u.map((e) => FrameLoggerImpl.formatValue(e)).join(', ')}]`
      }
      for (const specialFormatter of specialFormatters) {
        const line = specialFormatter(u);
        if (line) {
          return line;
        }
      }

      return `{ ${Object.entries(u).map(([key, value]) => `${key}=${FrameLoggerImpl.formatValue(value)}`).join(', ')} }`;

    }
    return JSON.stringify(u);
  }
}

function fractionFormatter(u: unknown): string | null {
  const validation = validateFraction(u);
  if (validation.errorMsg === null) {
    return `f(${approximateFractionValue(validation.value).toFixed(3)}, ${formatFractionAsExpression(validation.value)})` ;
  }
  return null;
}

const specialFormatters : ((u: unknown) => string | null)[] = [
  fractionFormatter,
];

